import {
	AgentChatWidgetQueryService,
	type SystemAgentWidgetData,
} from '@supply-mover-2-0/core';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
	type ReactNode,
} from 'react';

interface AgentChatDataContextValue {
	systemAgentData: SystemAgentWidgetData | null;
	isLoading: boolean;
	refresh: () => Promise<void>;
}

const AgentChatDataContext = createContext<AgentChatDataContextValue | null>(
	null,
);

export interface AgentChatDataProviderProps {
	children: ReactNode;
}

/**
 * Provider that pre-fetches the system AI agent for the chat widget at app
 * initialization. This ensures the agent data is immediately available when
 * the navigation menu opens — avoids a flash of missing widget on first paint.
 */
export const AgentChatDataProvider: React.FC<AgentChatDataProviderProps> = ({
	children,
}) => {
	const [systemAgentData, setSystemAgentData] =
		useState<SystemAgentWidgetData | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	const refresh = useCallback(async () => {
		setIsLoading(true);
		try {
			const data = await AgentChatWidgetQueryService.getSystemAgent(true);
			setSystemAgentData(data);
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		void AgentChatWidgetQueryService.getSystemAgent().then((data) => {
			setSystemAgentData(data);
			setIsLoading(false);
		});
	}, []);

	return (
		<AgentChatDataContext.Provider
			value={{
				systemAgentData,
				isLoading,
				refresh,
			}}
		>
			{children}
		</AgentChatDataContext.Provider>
	);
};

/**
 * Hook to access pre-fetched agent chat data.
 * Returns null if not within an AgentChatDataProvider.
 */
export const useAgentChatData = (): AgentChatDataContextValue | null =>
	useContext(AgentChatDataContext);

/**
 * Hook to safely access pre-fetched agent chat data.
 * This is an alias for useAgentChatData for consistency with other safe hooks.
 */
export const useAgentChatDataSafe = (): AgentChatDataContextValue | null =>
	useContext(AgentChatDataContext);
